import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip, Chip, IconButton } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getStatusConfig, getCapacityColor } from '../utils/bookingStatus';
import ScheduleScroller from './ScheduleScroller';
import {
  clipToWindow,
  minutesToTime,
  slotStarts,
  usedCapacityAt,
  isSlotBlockedAt,
  isBlockingSlot,
  isBusinessMinute,
  breakBands,
  isInBands,
  localIsoDate,
  packLanes,
  BREAK_HATCHING,
  RESOURCE_SLOT_WIDTH,
  RESOURCE_LANE_HEIGHT,
  RESOURCE_GUTTER_WIDTH,
  RESOURCE_GUTTER_COLLAPSED_WIDTH,
  SLOT_MINUTES,
} from '../utils/scheduleUtils';

const HEADER_HEIGHT = 40;

const makeGutterSx = (width) => ({
  flex: `0 0 ${width}px`,
  width: `${width}px`,
  position: 'sticky',
  left: 0,
  // Above the tracks' "now" line (5): sticky opens a stacking context, and a tie would go to
  // the later DOM node, which is the track.
  zIndex: 6,
  bgcolor: '#fff',
  borderRight: '1px solid #e0e0e0',
  display: 'flex',
  alignItems: 'center',
  px: width > RESOURCE_GUTTER_COLLAPSED_WIDTH ? 1.5 : 0.5,
  boxSizing: 'border-box',
  transition: 'flex-basis 0.15s, width 0.15s',
});

// Text in a booking block wraps instead of ending in an ellipsis. `anywhere` also breaks a
// single word too long for the block, so nothing runs out of its right edge.
const wrapSx = { whiteSpace: 'normal', overflowWrap: 'anywhere', minWidth: 0 };

/**
 * One run of adjacent lanes of a booking. A booking whose lanes are not adjacent comes as several
 * blocks, each showing everything; only the first is `schedule-booking`, so a booking still has
 * exactly one of those.
 */
const BookingBlock = ({ placement, run, isFirstPart, gridWindow, onClick }) => {
  const { slot, start, end, demand, overflow } = placement;
  const config = getStatusConfig(slot.bookingStatus);
  // Only the part on the grid is drawn; the text still gives the booking's real times.
  const visible = clipToWindow(gridWindow, start, end);
  const left = ((visible.start - gridWindow.start) / SLOT_MINUTES) * RESOURCE_SLOT_WIDTH;
  const width = ((visible.end - visible.start) / SLOT_MINUTES) * RESOURCE_SLOT_WIDTH;
  const height = run.count * RESOURCE_LANE_HEIGHT - 6;
  const StatusIcon = config.Icon;

  // Every block shows everything: its status chip, the guest, the service and the time. The
  // grid is sized for it (RESOURCE_LANE_HEIGHT / RESOURCE_SLOT_WIDTH) instead of the block
  // dropping whatever did not fit. Longer names wrap onto a second line rather than being cut.
  // The width only decides where the time goes: beside the chip when there is room, on a row
  // of its own when there is not.
  const isWide = width >= 200;
  // A blocked period closes everything; a device count would suggest it needs some.
  const showDevices = demand > 1 && !isBlockingSlot(slot);
  const timeRange = `${slot.startTime}–${slot.endTime}${showDevices ? ` · ${demand} eszköz` : ''}`;

  return (
    <Box
      data-testid={isFirstPart ? 'schedule-booking' : 'schedule-booking-part'}
      data-booking-id={slot.id}
      data-status={slot.bookingStatus}
      onClick={() => onClick && onClick(slot.id)}
      sx={{
        position: 'absolute',
        top: 3,
        left: left + 3,
        width: Math.max(width - 6, 24),
        height,
        bgcolor: config.bg,
        border: `1.5px solid ${config.border}`,
        borderLeft: `3px ${overflow ? 'double' : 'solid'} ${config.border}`,
        borderRadius: '6px',
        px: 1,
        py: 0.5,
        cursor: 'pointer',
        overflow: 'hidden',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
        gap: 0.25,
        boxSizing: 'border-box',
        transition: 'box-shadow 0.15s, transform 0.15s',
        '&:hover': {
          boxShadow: `0 2px 8px ${config.border}66`,
          transform: 'translateY(-1px)',
          zIndex: 3,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flexShrink: 0 }}>
        <Chip
          icon={React.createElement(StatusIcon, { sx: { fontSize: 11 } })}
          label={config.label}
          size="small"
          sx={{
            bgcolor: config.chipBg,
            color: '#fff',
            fontSize: '10px',
            // Auto rather than a fixed 18px, with a wrapping label: on a block narrower than
            // a slot — only a custom inactive period makes one — the label takes a second
            // line instead of being cut.
            height: 'auto',
            minHeight: '18px',
            fontWeight: 600,
            flexShrink: 0,
            maxWidth: '100%',
            '& .MuiChip-icon': { color: '#fff', ml: '4px' },
            '& .MuiChip-label': { px: '5px', py: '1px', whiteSpace: 'normal', lineHeight: 1.3 },
          }}
        />
        {isWide && (
          <Typography
            noWrap
            sx={{ fontSize: '10px', color: '#666', lineHeight: 1.2, ml: 'auto', fontVariantNumeric: 'tabular-nums' }}
          >
            {timeRange}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
        <PersonIcon sx={{ fontSize: 12, color: '#555', flexShrink: 0, mt: '2px' }} />
        <Typography sx={{ fontSize: '12.5px', color: '#222', fontWeight: 600, lineHeight: 1.3, ...wrapSx }}>
          {slot.guestName}
        </Typography>
      </Box>

      {slot.supplyName && (
        <Typography
          sx={{ fontSize: '10.5px', color: '#555', fontStyle: 'italic', lineHeight: 1.3, pl: '17px', ...wrapSx }}
        >
          {slot.supplyName}
        </Typography>
      )}

      {!isWide && (
        <Typography
          sx={{ fontSize: '10px', color: '#666', lineHeight: 1.2, pl: '17px', fontVariantNumeric: 'tabular-nums', ...wrapSx }}
        >
          {timeRange}
        </Typography>
      )}
    </Box>
  );
};

/**
 * One day of a resource tenant, one row per unit of every resource type.
 *
 * The lanes are a display packing over a unit count, not real device identity — see
 * DECISIONS.md D13. What is exact is the free/total count in each section header.
 */
export const ResourceDayBoard = ({
  scrollRef,
  dailySchedule,
  resourceTypes,
  gridWindow,
  onBookedSlotClick,
  onFreeSlotClick,
}) => {
  const timeSlots = dailySchedule.timeSlots || [];
  const trackWidth = gridWindow.slotCount * RESOURCE_SLOT_WIDTH;

  const isToday = dailySchedule.date === localIsoDate();

  const currentLeftIfVisible = () => {
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    if (minutes < gridWindow.start || minutes >= gridWindow.end) return null;
    return ((minutes - gridWindow.start) / SLOT_MINUTES) * RESOURCE_SLOT_WIDTH;
  };

  const [currentTimeLeft, setCurrentTimeLeft] = useState(isToday ? currentLeftIfVisible() : null);

  useEffect(() => {
    if (!isToday) return undefined;

    let interval;
    const tick = () => setCurrentTimeLeft(currentLeftIfVisible());
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    const timeout = setTimeout(() => {
      tick();
      interval = setInterval(tick, 60_000);
    }, msUntilNextMinute);

    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToday, gridWindow.start, gridWindow.end]);

  const [gutterCollapsed, setGutterCollapsed] = useState(false);
  const gutterWidth = gutterCollapsed ? RESOURCE_GUTTER_COLLAPSED_WIDTH : RESOURCE_GUTTER_WIDTH;
  const gutterSx = makeGutterSx(gutterWidth);

  const starts = slotStarts(gridWindow);
  // The tenant's daily breaks on this day (DECISIONS.md D32), as bands across every lane.
  const bands = breakBands(gridWindow, dailySchedule.date);

  const renderTrackBackground = () => (
    <React.Fragment>
      {/* Outside opening hours: still bookable by an admin, but visibly not business time. */}
      {starts
        .map((minutes, i) =>
          isBusinessMinute(gridWindow, minutes, dailySchedule.date) ? null : (
            <Box
              key={`closed-${minutes}`}
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: i * RESOURCE_SLOT_WIDTH,
                width: RESOURCE_SLOT_WIDTH,
                bgcolor: '#f0f0f2',
                zIndex: 0,
              }}
            />
          )
        )
        .filter(Boolean)}
      {/* No guest can book into a break, an admin still can, so the band only shades. */}
      {bands.map((band) => (
        <Box
          key={`break-${band.start}`}
          data-testid="schedule-break"
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: ((band.start - gridWindow.start) / SLOT_MINUTES) * RESOURCE_SLOT_WIDTH,
            width: ((band.end - band.start) / SLOT_MINUTES) * RESOURCE_SLOT_WIDTH,
            backgroundImage: BREAK_HATCHING,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      ))}
      {Array.from({ length: gridWindow.slotCount + 1 }, (_, i) => (
        <Box
          key={`grid-${i}`}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: i * RESOURCE_SLOT_WIDTH,
            width: '1px',
            bgcolor: i % 2 === 0 ? '#d8d8d8' : '#eee',
            zIndex: 0,
          }}
        />
      ))}
    </React.Fragment>
  );

  // The board's header row: it stays at the top while the lanes scroll under it.
  const timeAxis = (
    <Box sx={{ display: 'flex', bgcolor: '#f0f2f5', borderBottom: '1px solid #e0e0e0' }}>
      <Box sx={{ ...gutterSx, bgcolor: '#f0f2f5', zIndex: 7, justifyContent: 'space-between', gap: 0.5 }}>
        {!gutterCollapsed && (
          <Typography sx={{ fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#888' }}>
            Eszköz
          </Typography>
        )}
        <Tooltip title={gutterCollapsed ? 'Eszköznév oszlop mutatása' : 'Eszköznév oszlop összecsukása'}>
          <IconButton
            size="small"
            aria-label={gutterCollapsed ? 'Eszköznév oszlop mutatása' : 'Eszköznév oszlop összecsukása'}
            onClick={() => setGutterCollapsed((collapsed) => !collapsed)}
            sx={{ p: 0.25, color: '#777', mx: gutterCollapsed ? 'auto' : 0 }}
          >
            {gutterCollapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ position: 'relative', flex: '1 0 auto', width: trackWidth, height: '34px' }}>
        {starts.map((minutes, i) => (
          <Box
            key={minutes}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: i * RESOURCE_SLOT_WIDTH,
              width: RESOURCE_SLOT_WIDTH,
              borderLeft: minutes % 60 === 0 ? '1px solid #d8d8d8' : '1px solid #eee',
              display: 'flex',
              alignItems: 'center',
              pl: '7px',
            }}
          >
            <Typography
              sx={{
                fontSize: minutes % 60 === 0 ? '11px' : '10px',
                fontWeight: isBusinessMinute(gridWindow, minutes, dailySchedule.date) && minutes % 60 === 0 ? 600 : 400,
                color: isBusinessMinute(gridWindow, minutes, dailySchedule.date)
                  ? minutes % 60 === 0
                    ? '#555'
                    : '#999'
                  : '#c4c4c4',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {minutesToTime(minutes)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <ScheduleScroller ref={scrollRef} header={timeAxis}>
      {resourceTypes.map((resourceType, sectionIndex) => {
        const capacity = resourceType.totalCapacity || 0;
        // Packed over the whole day, so a booking keeps its lane whichever hours are shown; drawn
        // only if some of it is on the grid.
        const placements = packLanes(timeSlots, resourceType.id, capacity);
        const drawnPlacements = placements.filter((p) => clipToWindow(gridWindow, p.start, p.end));

        return (
          <React.Fragment key={resourceType.id}>
            {/* section header: per-slot free capacity */}
            <Box sx={{ display: 'flex', borderTop: '1px solid #e0e0e0' }}>
              <Box sx={gutterSx}>
                {gutterCollapsed ? (
                  <Tooltip title={`${resourceType.name} · ${capacity} eszköz`} placement="right">
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#222',
                        mx: 'auto',
                        cursor: 'default',
                      }}
                    >
                      {resourceType.name.charAt(0).toUpperCase()}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: '13px', fontWeight: 700, color: '#222' }}>
                      {resourceType.name}
                    </Typography>
                    <Typography sx={{ fontSize: '10px', color: '#999' }}>{capacity} eszköz</Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', width: trackWidth, height: HEADER_HEIGHT }}>
                {starts.map((minutes) => {
                  const blocked = isSlotBlockedAt(timeSlots, minutes);
                  const used = blocked ? capacity : usedCapacityAt(timeSlots, resourceType.id, minutes);
                  const free = Math.max(capacity - used, 0);
                  // A break is not bookable time either, so its counts are greyed rather
                  // than offering devices nobody can have.
                  const inBreak = isInBands(bands, minutes);
                  const inBusiness = isBusinessMinute(gridWindow, minutes, dailySchedule.date) && !inBreak;
                  const color = inBusiness
                    ? getCapacityColor(capacity === 0 ? 0 : free / capacity)
                    : '#b0b0b0';

                  return (
                    <Tooltip
                      key={minutes}
                      title={`${minutesToTime(minutes)} — ${free} szabad, ${used} foglalt eszköz (${resourceType.name})`}
                      placement="top"
                      arrow
                    >
                      <Box
                        sx={{
                          flex: `0 0 ${RESOURCE_SLOT_WIDTH}px`,
                          width: RESOURCE_SLOT_WIDTH,
                          borderLeft: '1px solid #eee',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: inBusiness ? `${color}1f` : '#f0f0f2',
                          // Hatched like the band below it, so a break reads as one column.
                          backgroundImage: inBreak ? BREAK_HATCHING : 'none',
                          boxSizing: 'border-box',
                        }}
                      >
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                          {free}
                        </Typography>
                        <Typography sx={{ fontSize: '9.5px', color: '#999', fontVariantNumeric: 'tabular-nums' }}>
                          / {capacity}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>

            {/* one row per unit */}
            {Array.from({ length: capacity }, (_, laneIndex) => (
              <Box
                key={`${resourceType.id}-${laneIndex}`}
                data-testid="resource-lane"
                data-resource-type={resourceType.id}
                sx={{ display: 'flex', borderTop: '1px solid #eee' }}
              >
                <Box sx={gutterSx}>
                  {gutterCollapsed ? (
                    <Tooltip title={`${resourceType.name} ${laneIndex + 1}`} placement="right">
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#777',
                          mx: 'auto',
                          fontVariantNumeric: 'tabular-nums',
                          cursor: 'default',
                        }}
                      >
                        {laneIndex + 1}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', border: '1.5px solid #bbb', flexShrink: 0 }} />
                      <Typography noWrap sx={{ fontSize: '12.5px', color: '#555' }}>
                        {resourceType.name} {laneIndex + 1}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ position: 'relative', flex: '1 0 auto', width: trackWidth, height: RESOURCE_LANE_HEIGHT, bgcolor: '#fafafa' }}>
                  {renderTrackBackground()}

                  {onFreeSlotClick &&
                    starts.map((minutes, slotIndex) => {
                      const takenHere = placements.some(
                        (p) => p.lanes.includes(laneIndex) && p.start <= minutes && p.end > minutes
                      );
                      if (takenHere || isSlotBlockedAt(timeSlots, minutes)) return null;

                      const startTime = minutesToTime(minutes);
                      const endTime = minutesToTime(minutes + SLOT_MINUTES);

                      return (
                        <Box
                          key={`free-${minutes}`}
                          onClick={() => onFreeSlotClick(dailySchedule.date, { startTime, endTime })}
                          sx={{
                            position: 'absolute',
                            top: 3,
                            bottom: 3,
                            left: slotIndex * RESOURCE_SLOT_WIDTH + 2,
                            width: RESOURCE_SLOT_WIDTH - 4,
                            borderRadius: '5px',
                            zIndex: 0,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'transparent',
                            fontSize: '11px',
                            fontWeight: 500,
                            transition: 'background 0.15s, color 0.15s',
                            '&:hover': { background: 'rgba(25, 118, 210, 0.08)', color: '#2196f3' },
                          }}
                        >
                          + {startTime}
                        </Box>
                      );
                    })}

                  {drawnPlacements.flatMap((p) =>
                    p.runs
                      .map((run, runIndex) =>
                        run.first === laneIndex ? (
                          <BookingBlock
                            key={`${p.slot.id}-${run.first}`}
                            placement={p}
                            run={run}
                            // A blocked period is drawn in every pool's section; only the first
                            // copy is its `schedule-booking`, so it still has exactly one.
                            isFirstPart={runIndex === 0 && (sectionIndex === 0 || !isBlockingSlot(p.slot))}
                            gridWindow={gridWindow}
                            onClick={onBookedSlotClick}
                          />
                        ) : null
                      )
                      .filter(Boolean)
                  )}

                  {currentTimeLeft !== null && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: currentTimeLeft,
                        width: '2px',
                        bgcolor: '#f44336',
                        zIndex: 5,
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                </Box>
              </Box>
            ))}
          </React.Fragment>
        );
      })}
    </ScheduleScroller>
  );
};

export default ResourceDayBoard;
